import { useQuery } from '@tanstack/react-query';
import {
  Block,
  BlockTitle,
  Button,
  Card,
  Chip,
  List,
  ListItem,
  Navbar,
  Page,
  Progressbar,
  Segmented,
  SegmentedButton,
} from 'konsta/react';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { UserTransaction } from '../models/Models';
import { Get } from '../helpers/AxiosHelper';
import { formatMinerUSD, formatUSD } from '../utils';
import moment from 'moment';
import TabBar from '../components/TabBar';
import { useAppContext } from '../context/AppContext';
import { UserProductActivityCd } from '../enums';
import { MdOutlineStorefront } from 'react-icons/md';
import { mixTrack } from '../helpers/MixPanelHelper';

const MyMinersPage = () => {
  const { userProducts, balance } = useAppContext();
  const [tab, setTab] = useState<'miners' | 'earnings'>('miners');
  const [totalProfitPerDay, setTotalProfitPerDay] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    if (userProducts) {
      let _totalProfitPerDay = 0;
      for (const up of userProducts) {
        _totalProfitPerDay += Number(up.product.avgProfitDay);
      }

      setTotalProfitPerDay(_totalProfitPerDay);
    }
  }, [userProducts, balance]);

  return (
    <Page className="select-none pb-40">
      <Navbar
        title={<h1 className="text-2xl">Total</h1>}
        right={
          <h1 className="text-3xl currency text-primary me-2">
            {balance > -1 ? formatMinerUSD(balance) : '-'}
          </h1>
        }
        className="top-0 sticky translucent"
        subnavbar={
          userProducts && userProducts.length > 0 ? (
            <Segmented strong>
              <SegmentedButton
                strong
                active={tab === 'miners'}
                onClick={() => setTab('miners')}
              >
                Miners
              </SegmentedButton>
              <SegmentedButton
                strong
                active={tab === 'earnings'}
                onClick={() => {
                  mixTrack('[View] Projected Earnings');
                  setTab('earnings');
                }}
              >
                Earnings
              </SegmentedButton>
            </Segmented>
          ) : undefined
        }
      />

      {tab === 'miners' && userProducts && userProducts.length > 0 && (
        <>
          <Block>
            <div className="mt-3">* Earnings are updated every 30 minutes</div>
          </Block>
          {userProducts.map((up) => (
            <Card
              raised
              onClick={() => {
                mixTrack('[View] View Miner', {
                  productId: up.product.id,
                  productName: up.product.name,
                  userProductId: up.id,
                  name: up.name,
                });
                navigate('/my-miners/' + up.id);
              }}
            >
              <div>
                <div className="flex justify-between">
                  <h1 className="text-lg font-semibold">{up.name}</h1>
                  {up.activityCd === UserProductActivityCd.Active && (
                    <div className="text-primary flex items-center">
                      Online{' '}
                      <span className="relative flex size-3 ms-3">
                        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary opacity-75"></span>
                        <span className="relative inline-flex size-3 rounded-full bg-primary"></span>
                      </span>
                    </div>
                  )}

                  {up.activityCd === UserProductActivityCd.SettingUp && (
                    <div className="text-cyan-400 flex items-center">
                      Setting up...
                    </div>
                  )}
                </div>
                <div>{up.product.name.substring(0, 50)}</div>
                {up.activityCd === UserProductActivityCd.Active && (
                  <div className="currency text-primary text-xl tracking-widest mt-2">
                    {formatMinerUSD(up.currentBalance)}
                  </div>
                )}

                {up.activityCd === UserProductActivityCd.SettingUp &&
                  up.setupProgress > -1 && (
                    <div>
                      <Progressbar
                        className="k-color-brand-green mt-3"
                        progress={
                          up.setupProgress > 0 ? up.setupProgress / 100 : 0
                        }
                      />
                      <span className="text-primary">
                        {up.setupProgress.toFixed(2)}%
                      </span>
                    </div>
                  )}
              </div>
            </Card>
          ))}
          <br />
          <br />
          <br />
        </>
      )}

      {tab === 'earnings' && (
        <>
          <BlockTitle>Approx. Projected Earnings</BlockTitle>
          <List strong>
            <ListItem
              title="Daily"
              after={
                <span className="currency text-primary tracking-widest">
                  {formatUSD(totalProfitPerDay)}
                </span>
              }
            />
            <ListItem
              title="Weekly"
              after={
                <span className="currency text-primary tracking-widest">
                  {formatUSD(totalProfitPerDay * 7)}
                </span>
              }
            />
            <ListItem
              title="Bi-weekly"
              after={
                <span className="currency text-primary tracking-widest">
                  {formatUSD(totalProfitPerDay * 14)}
                </span>
              }
            />
            <ListItem
              title="Monthly"
              after={
                <span className="currency text-primary tracking-widest">
                  {formatUSD(totalProfitPerDay * 30)}
                </span>
              }
            />
            <ListItem
              title="Annually"
              after={
                <span className="currency text-primary tracking-widest">
                  {formatUSD(totalProfitPerDay * 365)}
                </span>
              }
            />
          </List>
          <Block>
            The earnings displayed are estimates based on current market
            conditions. Cryptocurrency prices and mining rewards fluctuate due
            to market volatility, network difficulty, and other factors beyond
            our control. Actual earnings may vary and are not guaranteed. Please
            do your own research and consider market risks before making
            financial decisions.
          </Block>
        </>
      )}

      {userProducts && userProducts.length === 0 && (
        <Block className="text-center text-lg">
          No products found
          <div>
            <Button className="mt-2" onClick={() => navigate('/store')}>
              Buy a miner <MdOutlineStorefront className="ms-3 w-6 h-6" />
            </Button>
          </div>
        </Block>
      )}

      <TabBar selectedTab="miners" />
    </Page>
  );
};

export default MyMinersPage;
